import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Crown, X } from "lucide-react";

// Mounted from ProtectedRoute. Polls /api/plans/me on mount + every 5 min.
//
// Shown when:
//   - in_grace = true (within 7 days post-expiry)              → red urgent banner
//   - remind_renewal = true (within 30 days of expiry)         → saffron reminder
// Hidden when:
//   - free tier, no expiry
//   - actively on /plans page (avoid noise on the actual page)
//   - user dismissed it (sticks for 24h via localStorage)
const DISMISS_KEY = "vt_renewal_dismissed_at";
const DISMISS_HOURS = 24;
const POLL_MS = 5 * 60 * 1000;

function recentlyDismissed() {
    try {
        const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
        return ts && (Date.now() - ts) < DISMISS_HOURS * 3600 * 1000;
    } catch {
        return false;
    }
}

export function PlanRenewalBanner() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [plan, setPlan] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetch = async () => {
            try {
                const r = await api.get("/plans/me");
                if (!cancelled) setPlan(r.data);
            } catch { /* ignore */ }
        };
        fetch();
        const id = setInterval(fetch, POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    if (!plan) return null;
    if (pathname === "/plans") return null;
    if (recentlyDismissed() || dismissed) return null;

    const showGrace = plan.in_grace;
    const showRemind = !showGrace && plan.remind_renewal;
    if (!showGrace && !showRemind) return null;

    const days = plan.days_until_expiry;
    const tone = showGrace
        ? { bg: "#FFEBEE", border: "#C62828", text: "#B71C1C", cta: "#C62828" }
        : { bg: "#FFF3E0", border: "#F57C00", text: "#7A3E00", cta: "#F57C00" };

    const headline = showGrace
        ? `Your ${plan.label} plan expired ${days != null ? Math.abs(Math.round(days)) : "a few"} days ago — 7-day grace ending soon.`
        : `Your ${plan.label} plan expires in ${days != null ? Math.round(days) : "<30"} days. Renew now to avoid losing access.`;

    const onDismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            data-testid={showGrace ? "plan-grace-banner" : "plan-renewal-banner"}
            role="status"
            className="sticky top-0 z-[60] w-full px-4 py-2 text-[13.5px] flex items-center justify-center gap-3 shadow-sm border-b"
            style={{ background: tone.bg, borderColor: tone.border, color: tone.text }}
        >
            <Crown className="h-4 w-4 shrink-0" />
            <span className="font-medium truncate">{headline}</span>
            <button
                type="button"
                data-testid="plan-renewal-cta"
                onClick={() => navigate("/plans")}
                className="font-semibold rounded-md px-3 py-1 text-white shrink-0"
                style={{ background: tone.cta }}
            >
                Renew now
            </button>
            <button
                type="button"
                aria-label="Dismiss"
                data-testid="plan-renewal-dismiss"
                onClick={onDismiss}
                className="opacity-60 hover:opacity-100 shrink-0"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}

export default PlanRenewalBanner;
